import React, { useState } from 'react';
import { DocumentArrowDownIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import reportsService from '../services/reports.service';
import { TipoReporte, GenerateReportRequest } from '../types';

const Reportes: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<GenerateReportRequest>({
    tipo: 'clientes',
    formato: 'excel',
    fechaInicio: '',
    fechaFin: '',
  });

  const getReportDescription = (tipo: TipoReporte): string => {
    const descriptions = {
      'clientes': 'Reporte general de todos los clientes registrados en el sistema.',
      'contratos': 'Reporte de contratos con información detallada de fechas y valores.',
      'pagos': 'Reporte de pagos reales realizados por los clientes.',
      'propuestas': 'Reporte de propuestas comerciales enviadas a clientes.',
      'pagos-programados': 'Reporte de pagos programados con su estado actual. Útil para seguimiento de cobros pendientes.',
      'pagos-reales-programados': 'Reporte de pagos reales vinculados con pagos programados. Permite comparar fechas programadas vs reales.',
      'clientes-sin-contratos': 'Reporte de clientes activos que no tienen contratos asignados. Útil para identificar oportunidades de negocio.'
    };
    return descriptions[tipo];
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenerateReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await reportsService.downloadReport(formData);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error al generar el reporte. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Reportes</h1>
        <p className="mt-2 text-sm text-gray-700">
          Genera reportes en PDF o Excel de los datos del CRM
        </p>
      </div>

      <div className="max-w-2xl">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">
            Generar Reporte
          </h2>

          <form onSubmit={handleGenerateReport} className="space-y-6">
            {/* Tipo de Reporte */}
            <div>
              <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Reporte
              </label>
              <select
                id="tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <optgroup label="Reportes Generales">
                  <option value="clientes">Clientes</option>
                  <option value="contratos">Contratos</option>
                  <option value="pagos">Pagos Reales</option>
                  <option value="propuestas">Propuestas</option>
                </optgroup>
                <optgroup label="Reportes Especializados">
                  <option value="pagos-programados">Pagos Programados</option>
                  <option value="pagos-reales-programados">Pagos Reales con Programados</option>
                  <option value="clientes-sin-contratos">Clientes sin Contratos</option>
                </optgroup>
              </select>
              
              {/* Descripción del reporte */}
              <div className="mt-2 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  {getReportDescription(formData.tipo)}
                </p>
              </div>
            </div>

            {/* Formato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Formato
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none">
                  <input
                    type="radio"
                    name="formato"
                    value="excel"
                    checked={formData.formato === 'excel'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-1">
                    <div className="flex items-center">
                      <TableCellsIcon className="h-6 w-6 text-green-600 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Excel</div>
                        <div className="text-xs text-gray-500">Archivo .xlsx</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`absolute -inset-px rounded-lg border-2 pointer-events-none ${
                      formData.formato === 'excel' ? 'border-primary-500' : 'border-transparent'
                    }`}
                  />
                </label>

                <label className="relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none">
                  <input
                    type="radio"
                    name="formato"
                    value="pdf"
                    checked={formData.formato === 'pdf'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div className="flex flex-1">
                    <div className="flex items-center">
                      <DocumentArrowDownIcon className="h-6 w-6 text-red-600 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">PDF</div>
                        <div className="text-xs text-gray-500">Archivo .pdf</div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`absolute -inset-px rounded-lg border-2 pointer-events-none ${
                      formData.formato === 'pdf' ? 'border-primary-500' : 'border-transparent'
                    }`}
                  />
                </label>
              </div>
            </div>

            {/* Rango de Fechas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="fechaInicio" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  id="fechaInicio"
                  name="fechaInicio"
                  value={formData.fechaInicio}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="fechaFin" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Fin
                </label>
                <input
                  type="date"
                  id="fechaFin"
                  name="fechaFin"
                  value={formData.fechaFin}
                  onChange={handleInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generando...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                    Generar Reporte
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Info Cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Reportes en Excel
            </h3>
            <p className="text-sm text-blue-700">
              Perfectos para análisis de datos y cálculos. Incluye todas las columnas de información.
            </p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-red-900 mb-2">
              Reportes en PDF
            </h3>
            <p className="text-sm text-red-700">
              Ideales para presentaciones e impresión. Formato profesional y elegante.
            </p>
          </div>
        </div>

        {/* Nuevos reportes disponibles */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Reportes Especializados Disponibles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-2">
                Pagos Programados
              </h4>
              <p className="text-sm text-green-700">
                Lista todos los pagos programados con su estado. Ideal para seguimiento de cobros pendientes.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">
                Pagos Reales vs Programados
              </h4>
              <p className="text-sm text-yellow-700">
                Compara pagos reales con sus programaciones originales. Útil para análisis de cumplimiento.
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-purple-900 mb-2">
                Clientes sin Contratos
              </h4>
              <p className="text-sm text-purple-700">
                Identifica clientes activos sin contratos. Perfecto para encontrar oportunidades de negocio.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reportes;